import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, MessageSquare, AlertCircle, Loader2 } from 'lucide-react';
import { feedbackService, FeedbackType } from '../../services/feedbackService';
import { toast } from 'sonner';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  screenshot: string | null;
  initialType?: FeedbackType;
  initialMessage?: string;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ 
  isOpen, 
  onClose, 
  screenshot,
  initialType,
  initialMessage
}) => {
  const [type, setType] = useState<FeedbackType>(initialType || 'suggestion');
  const [message, setMessage] = useState(initialMessage || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update local state when initial props change (e.g. when modal is opened via event)
  useEffect(() => {
    if (isOpen) {
      setType(initialType || 'suggestion');
      setMessage(initialMessage || '');
    }
  }, [isOpen, initialType, initialMessage]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message && !screenshot) {
      toast.error('Por favor, escribe un mensaje o envía una captura.');
      return;
    }

    setIsSubmitting(true);
    console.log('[FeedbackModal] Starting submission...', { type, messageLength: message.length, hasScreenshot: !!screenshot });
    if (screenshot) {
      console.log('[FeedbackModal] Screenshot size:', Math.round(screenshot.length / 1024), 'KB');
    }
    
    try {
      const result = await feedbackService.submitFeedback(type, message, screenshot);
      console.log('[FeedbackModal] Submission successful, result:', result);
      toast.success('¡Gracias! Tu comentario se ha enviado correctamente.');
      onClose();
      setMessage('');
    } catch (error: any) {
      console.error('[FeedbackModal] Error submitting feedback:', error);
      let errorMessage = error.message || 'Error desconocido';
      
      // Try to parse JSON error from firestoreErrorHandler
      try {
        if (errorMessage.startsWith('{')) {
          const errObj = JSON.parse(errorMessage);
          errorMessage = `${errObj.error} (${errObj.operationType} en ${errObj.path})`;
        }
      } catch (e) {
        // Not JSON, use original message
      }
      
      toast.error(`No se pudo enviar: ${errorMessage}`);
    } finally {
      console.log('[FeedbackModal] Submission process finished.');
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 feedback-modal"
          style={{ perspective: '1000px' }} // Isolated from app perspective
        >
          {/* Backdrop - No animation on the container itself to keep it stable */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm feedback-modal-overlay"
          />

          {/* Modal Panel - Animate only scale and opacity */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-card-bg rounded-[32px] border border-grid-line shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] z-10"
          >
            {/* Header */}
            <div className="p-6 border-b border-grid-line flex items-center justify-between bg-board-bg/50 shrink-0">
              <div>
                <h2 className="text-xl font-black text-text-primary tracking-tight">Enviar comentario</h2>
                <p className="text-[11px] text-text-secondary font-bold leading-tight mt-1">
                  Cuéntanos qué mejorarías, qué no te convence o qué problema has encontrado.
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-xl bg-card-bg flex items-center justify-center hover:bg-board-bg transition-all border border-grid-line text-text-secondary hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content - Scrollable area */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
              {/* Type Selector */}
              <div className="flex gap-3 p-1 bg-board-bg/50 rounded-2xl border border-grid-line">
                <button
                  type="button"
                  onClick={() => setType('suggestion')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-black transition-all ${
                    type === 'suggestion' 
                      ? 'bg-mult-tl text-white shadow-lg' 
                      : 'text-text-secondary hover:bg-card-bg'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  Sugerencia
                </button>
                <button
                  type="button"
                  onClick={() => setType('problem')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-black transition-all ${
                    type === 'problem' 
                      ? 'bg-mult-tp text-white shadow-lg' 
                      : 'text-text-secondary hover:bg-card-bg'
                  }`}
                >
                  <AlertCircle className="w-4 h-4" />
                  Problema
                </button>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe tu idea, mejora o problema..."
                  className="w-full h-32 p-4 bg-board-bg/30 rounded-2xl border border-grid-line text-sm text-text-primary font-bold placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-accent/50 transition-all resize-none"
                />
              </div>

              {/* Screenshot Preview */}
              {screenshot && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-text-muted uppercase tracking-widest px-1">Captura de pantalla</p>
                  <div className="relative aspect-video rounded-2xl overflow-hidden border border-grid-line bg-board-bg/50 w-full">
                    <img 
                      src={screenshot} 
                      alt="Screenshot" 
                      className="w-full h-full object-cover" 
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-3">
                      <span className="text-[9px] text-white/80 font-bold uppercase tracking-tighter">Captura automática generada</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2 shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-4 rounded-2xl bg-card-bg border border-grid-line text-sm font-black text-text-secondary hover:bg-board-bg transition-all active:scale-95"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-[2] py-4 rounded-2xl bg-primary-accent text-white text-sm font-black shadow-lg shadow-primary-accent/20 hover:brightness-110 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};
